import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { forwardRef } from "react";
import cn from "../../../utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 box-border whitespace-nowrap rounded-lg text-xs md:text-base md:leading-6 leading-[1.125rem] font-medium focus-visible:outline-none focus-visible:ring-4 disabled:pointer-events-none md:[&_svg]:size-5 [&_svg]:size-[0.875rem] [&_svg]:shrink-0",
  {
    variants: {
      color: {
        mandarin:
          "text-mandarin-900 bg-mandarin-50 [&_svg]:stroke-mandarin-600",
        indigo: true,
      },
      size: {
        md: "py-2.5 px-5 h-11",
      },
      variant: {
        contained: "disabled:bg-gray-400 disabled:text-gray-50",
        text: "disabled:text-gray-300 p-0",
      },
    },
    compoundVariants: [
      {
        variant: "text",
        color: "indigo",
        className:
          "text-indigo-600 hover:text-indigo-400 focus-visible:text-indigo-400 active:text-indigo-800",
      },
      {
        variant: "contained",
        color: "indigo",
        className: "bg-indigo-500 text-white",
      },
    ],
    defaultVariants: {
      color: "mandarin",
      size: "md",
      variant: "contained",
    },
  }
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      color,
      size,
      asChild = false,
      startIcon,
      endIcon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(buttonVariants({ variant, color, size, className }))}
        ref={ref}
        {...props}
      >
        {startIcon}
        {children}
        {endIcon}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
